/**
 * What /preview/3d/:artifactId does (the interactive 3D model viewer page, Tier-21 deliverable, 2026-08-01 Miles
 * directive; lifted out of src/components/FilePreview/Preview3DPage.tsx unchanged, PV5-8:C part 1, 2026-10-13):
 * the model resolved by its id (useThreeDScene), the frame made full screen, the scene saved as its HTML file, and
 * back. The page draws what this answers.
 */
import React, { useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ResolvedScene } from '../../components/FilePreview/resolve3d';
import { useThreeDScene, type ThreeDSceneFailure } from './useFloatingPreviewWindow';

export interface ThreeDPreviewPageView {
  scene: ResolvedScene | null;
  failure: ThreeDSceneFailure | null;
  frameWrapRef: React.MutableRefObject<HTMLDivElement | null>;
  fullscreen: () => void;
  downloadScene: () => void;
  back: () => void;
}

export function useThreeDPreviewPage(): ThreeDPreviewPageView {
  const { artifactId } = useParams<{ artifactId: string }>();
  const navigate = useNavigate();
  const { scene, failure } = useThreeDScene(String(artifactId || ''));
  const frameWrapRef = useRef<HTMLDivElement | null>(null);

  const fullscreen = useCallback(() => {
    frameWrapRef.current?.requestFullscreen?.().catch(() => undefined);
  }, []);

  const downloadScene = useCallback(() => {
    if (!scene) return;
    const blob = new Blob([scene.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = scene.name || 'model.3d.html';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [scene]);

  return { scene, failure, frameWrapRef, fullscreen, downloadScene, back: () => navigate(-1) };
}
